import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MdRefresh, MdClose, MdTimer, MdCheck, MdVolumeUp,
  MdNotifications, MdDeliveryDining, MdStorefront,
  MdAdd, MdRemove, MdDelete, MdAccessTime, MdLocationOn,
  MdPayment, MdCircle, MdSwapHoriz, MdPrint,
} from "react-icons/md";
import { IoFastFood } from "react-icons/io5";
import { useAuth } from "contexts/AuthContext";
import {
  getOrders, updateOrderStatus, markOrderReady, cancelOrder, setPreparationTime,
  addOrderProduct, updateOrderProduct, deleteOrderProduct, getOrderDetail,
} from "services/ordersService";
import { getMenus, getCategories, getProducts, getProductOptions, setProductAvailability } from "services/menuService";
import { useNewOrderAlert } from "hooks/useNewOrderAlert";
import { printOrderTicket } from "utils/printReceipt";
import OperationalStatusBar from "components/OperationalStatusBar";

// ── Status registry ───────────────────────────────────────────────────────────
const STATUS = {
  1: { label: "Nouveau",        color: "bg-blue-100 text-blue-700",    dot: "bg-blue-500"   },
  2: { label: "Confirmé",       color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-500" },
  3: { label: "En préparation", color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  4: { label: "En route",       color: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  5: { label: "Livré",          color: "bg-green-100 text-green-700",  dot: "bg-green-500"  },
  6: { label: "Refusé",         color: "bg-red-100 text-red-600",      dot: "bg-red-500"    },
};

const STATUS_GROUPS = [
  { label: "Nouveaux",          ids: [1],    border: "border-blue-500"   },
  { label: "En cours",          ids: [2, 3], border: "border-orange-500" },
  { label: "En route / Livrés", ids: [4, 5], border: "border-green-500"  },
  { label: "Refusés",           ids: [6],    border: "border-red-400"    },
];

const PREP_OPTIONS = [10, 15, 20, 30, 45, 60];

function fmt(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleString("fr-FR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (data?.data?.id) return [data.data];
  if (data?.id) return [data];
  return [];
}

function localDateString(date = new Date()) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function orderReference(order) {
  return order?.ref || order?.reference || "Référence indisponible";
}

function orderReceptionMode(order) {
  return order?.restaurant?.order_reception_mode
    || order?.restaurant?.orderReceptionMode
    || order?.order_reception_mode;
}

function orderReceptionLabel(mode) {
  if (mode === "application") return "Application / Web";
  if (mode === "telephone") return "Téléphone";
  return "Non renseigné";
}

function orderReceptionClass(mode) {
  if (mode === "application") return "bg-green-100 text-green-700";
  if (mode === "telephone") return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-500";
}

function getProductName(product) {
  return product?.name || product?.product?.name || "Produit";
}

function getPivotId(product) {
  return product?.pivot?.id || product?.orderProductId || product?.id;
}

function getItemIds(product) {
  const raw = product?.items || product?.pivot?.items || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => Number(item?.id ?? item))
    .filter((id) => Number.isFinite(id));
}

// ── OrderCard (kanban card) ───────────────────────────────────────────────────
function OrderCard({ order, onAccept, onRefuse, onPrepare, onReady, onSelect, onPrint }) {
  const st       = STATUS[order.status_id] || STATUS[1];
  const total    = order.total_price || order.total || 0;
  const canAccept  = order.status_id === 1;
  const canPrepare = order.status_id === 2;
  const canReady   = order.status_id === 3 && !order.order_ready_on;
  const canPrint   = order.status_id >= 2; // accepted and beyond

  // Urgency indicator — how long ago was this new order placed?
  const minutesSince = canAccept && order.created_at
    ? Math.floor((Date.now() - new Date(order.created_at)) / 60000)
    : null;

  return (
    <div
      className={`cursor-pointer rounded-xl border bg-white p-4 shadow-sm hover:shadow-md dark:bg-navy-800 transition-shadow ${
        canAccept ? "border-blue-200 dark:border-blue-900/50" : "border-gray-100 dark:border-navy-700"
      }`}
      onClick={() => onSelect(order)}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400">#{orderReference(order)}</span>
            {order.orderType === "takeaway" && (
              <span className="rounded-full bg-amber-100 text-amber-600 text-[10px] font-bold px-1.5 py-0.5">
                À emporter
              </span>
            )}
          </div>
          <p className="font-bold text-navy-700 dark:text-white text-sm mt-0.5">
            {order.user?.first_name
              ? `${order.user.first_name} ${order.user.last_name || ""}`.trim()
              : order.user?.name || "Client"}
          </p>
          {minutesSince !== null && minutesSince >= 5 && (
            <p className={`text-[10px] font-bold mt-0.5 ${minutesSince >= 15 ? "text-red-500" : "text-orange-500"}`}>
              ⚡ Attend depuis {minutesSince} min
            </p>
          )}
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${st.color}`}>
          {st.label}
        </span>
      </div>

      <div className="mb-3 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
        {(order.products || []).slice(0, 2).map((p, i) => (
          <p key={i}>• {p.name || p.product?.name} ×{p.quantity}</p>
        ))}
        {(order.products || []).length > 2 && (
          <p className="text-brand-500">+{order.products.length - 2} autres</p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="font-bold text-navy-700 dark:text-white text-sm">
          {Number(total).toFixed(3)} TND
        </span>
        <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          {canAccept && (
            <>
              <button
                className="flex items-center gap-1 rounded-lg bg-green-500 px-2.5 py-1 text-xs font-bold text-white hover:bg-green-600"
                onClick={() => onAccept(order)}
              >
                <MdCheck className="h-3.5 w-3.5" />
                Accepter
              </button>
              <button
                className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-500 hover:bg-red-100"
                onClick={() => onRefuse(order)}
              >
                Refuser
              </button>
            </>
          )}
          {canPrepare && (
            <button
              className="flex items-center gap-1 rounded-lg bg-orange-500 px-2.5 py-1 text-xs font-bold text-white hover:bg-orange-600"
              onClick={() => onPrepare(order.id)}
            >
              <MdStorefront className="h-3.5 w-3.5" />
              En préparation
            </button>
          )}
          {canReady && (
            <button
              className="flex items-center gap-1 rounded-lg bg-green-500 px-2.5 py-1 text-xs font-bold text-white hover:bg-green-600"
              onClick={() => onReady(order.id)}
            >
              <MdCheck className="h-3.5 w-3.5" />
              Prete
            </button>
          )}
          {canPrint && (
            <button
              title="Imprimer le ticket"
              className="flex items-center justify-center rounded-lg border border-gray-200 bg-white px-2 py-1 text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              onClick={() => onPrint(order)}
            >
              <MdPrint className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {order.order_ready_on && (
        <p className="mt-2 flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
          <MdCheck className="h-3.5 w-3.5" />
          Commande prete a {fmt(order.order_ready_on)}
        </p>
      )}
    </div>
  );
}

// ── Accept modal ──────────────────────────────────────────────────────────────
function AcceptModal({ order, onConfirm, onClose }) {
  const [prepTime, setPrepTime] = useState(20);
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-navy-800 shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-navy-700 dark:text-white">Accepter la commande</h3>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-navy-700">
            <MdClose className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Commande <span className="font-bold text-navy-700 dark:text-white">#{orderReference(order)}</span>
          {" — "}{Number(order.total_price || order.total || 0).toFixed(3)} TND
        </p>
        <p className="text-xs font-semibold text-gray-400 uppercase mb-2 flex items-center gap-1">
          <MdTimer className="h-4 w-4" /> Temps de préparation estimé
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          {PREP_OPTIONS.map((m) => (
            <button
              key={m}
              className={`rounded-xl px-3 py-1.5 text-sm font-semibold transition-colors ${
                prepTime === m
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-white"
              }`}
              onClick={() => setPrepTime(m)}
            >
              {m} min
            </button>
          ))}
        </div>
        <button
          className="w-full rounded-xl bg-green-500 py-3 text-sm font-bold text-white hover:bg-green-600"
          onClick={() => onConfirm(order.id, prepTime)}
        >
          <MdCheck className="inline h-4 w-4 mr-1.5" />
          Confirmer — {prepTime} min
        </button>
      </div>
    </div>
  );
}

// ── Refuse modal ──────────────────────────────────────────────────────────────
function RefuseModal({ order, onConfirm, onClose }) {
  const [motif, setMotif] = useState("");
  const MOTIFS = ["Rupture de stock", "Fermeture exceptionnelle", "Zone non couverte", "Commande incomplète", "Autre"];
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-navy-800 shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-navy-700 dark:text-white">Refuser la commande</h3>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-navy-700">
            <MdClose className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Motif de refus *</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {MOTIFS.map((m) => (
            <button
              key={m}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors ${
                motif === m ? "bg-red-500 text-white" : "bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-white"
              }`}
              onClick={() => setMotif(m)}
            >
              {m}
            </button>
          ))}
        </div>
        <textarea
          className="w-full rounded-xl border border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-700 p-3 text-sm text-navy-700 dark:text-white resize-none mb-4"
          placeholder="Précisez le motif..."
          rows={2}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
        />
        <div className="flex gap-2">
          <button
            className="flex-1 rounded-xl bg-red-500 py-3 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-40"
            disabled={!motif.trim()}
            onClick={() => onConfirm(order.id, motif.trim())}
          >
            Confirmer le refus
          </button>
          <button
            className="rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-500"
            onClick={onClose}
          >
            Retour
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductPickerModal({ visible, restaurantId, replaceTarget, onClose, onConfirm, saving }) {
  const [step, setStep] = useState("browse");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [categoryId, setCategoryId] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [specifications, setSpecifications] = useState("");

  useEffect(() => {
    if (!visible) {
      setStep("browse");
      setSearch("");
      setSelectedProduct(null);
      setSelectedItems({});
      setQuantity(1);
      setSpecifications("");
      return;
    }
    if (!restaurantId) return;

    setLoadingMenu(true);
    getMenus(restaurantId)
      .then(async (menuData) => {
        const menu = normalizeList(menuData)[0];
        if (!menu?.id) {
          setCategories([]);
          setProductsByCategory({});
          setCategoryId(null);
          return;
        }

        const cats = normalizeList(await getCategories(menu.id));
        setCategories(cats);
        setCategoryId(cats[0]?.id || null);

        const nextProducts = {};
        await Promise.all(cats.map(async (cat) => {
          try {
            nextProducts[cat.id] = normalizeList(await getProducts(cat.id));
          } catch {
            nextProducts[cat.id] = [];
          }
        }));
        setProductsByCategory(nextProducts);
      })
      .catch(() => {
        setCategories([]);
        setProductsByCategory({});
      })
      .finally(() => setLoadingMenu(false));
  }, [visible, restaurantId]);

  if (!visible) return null;

  const allProducts = Object.values(productsByCategory).flat();
  const query = search.trim().toLowerCase();
  const displayedProducts = query
    ? allProducts.filter((product) => product.name?.toLowerCase().includes(query))
    : productsByCategory[categoryId] || [];
  const availableProducts = displayedProducts.filter((product) => product.available !== false && product.available !== 0);

  const itemKey = (optionId, itemId) => `${optionId}_${itemId}`;
  const getOptionItems = (option) => Array.isArray(option?.items) ? option.items : [];

  const toggleItem = (option, item) => {
    const key = itemKey(option.id, item.id);
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (option.is_size || option.max === 1) {
        getOptionItems(option).forEach((choice) => {
          delete next[itemKey(option.id, choice.id)];
        });
        if (!prev[key]) next[key] = true;
      } else if (next[key]) {
        delete next[key];
      } else {
        const selectedCount = getOptionItems(option).filter((choice) => next[itemKey(option.id, choice.id)]).length;
        if (option.max > 0 && selectedCount >= option.max) return prev;
        next[key] = true;
      }
      return next;
    });
  };

  const handleSelectProduct = async (product) => {
    let next = { ...product };
    if (!Array.isArray(next.options) || next.options.length === 0) {
      try {
        next.options = normalizeList(await getProductOptions(product.id));
      } catch {
        next.options = [];
      }
    }
    setSelectedProduct(next);
    setSelectedItems({});
    setQuantity(1);
    setSpecifications("");
    setStep("configure");
  };

  const calcTotal = () => {
    if (!selectedProduct) return 0;
    let unitPrice = Number(selectedProduct.price) || 0;
    (selectedProduct.options || []).forEach((option) => {
      getOptionItems(option).forEach((item) => {
        if (selectedItems[itemKey(option.id, item.id)]) {
          unitPrice += Number(item.price) || 0;
        }
      });
    });
    return unitPrice * quantity;
  };

  const isValid = () => {
    if (!selectedProduct) return false;
    return (selectedProduct.options || []).every((option) => {
      if (!option.min) return true;
      const selectedCount = getOptionItems(option).filter((item) => selectedItems[itemKey(option.id, item.id)]).length;
      return selectedCount >= Number(option.min);
    });
  };

  const handleConfirm = () => {
    const items = Object.keys(selectedItems)
      .filter((key) => selectedItems[key])
      .map((key) => Number(key.split("_")[1]))
      .filter((id) => Number.isFinite(id));

    onConfirm(selectedProduct.id, {
      quantity,
      items,
      specifications,
      total: calcTotal(),
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-100 p-4 dark:border-navy-700">
          <button
            className="rounded-xl px-3 py-2 text-sm font-semibold text-brand-500 hover:bg-brand-50 dark:hover:bg-navy-700"
            onClick={step === "configure" ? () => setStep("browse") : onClose}
          >
            {step === "configure" ? "Retour" : "Fermer"}
          </button>
          <h3 className="truncate px-3 text-base font-bold text-navy-700 dark:text-white">
            {replaceTarget ? `Remplacer ${replaceTarget.name}` : "Ajouter un article"}
          </h3>
          <div className="w-20" />
        </div>

        {step === "browse" ? (
          <>
            <div className="border-b border-gray-100 p-4 dark:border-navy-700">
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-navy-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                placeholder="Rechercher un produit..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {!query && categories.length > 0 && (
              <div className="flex gap-2 overflow-x-auto border-b border-gray-100 px-4 py-3 dark:border-navy-700">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
                      categoryId === cat.id
                        ? "bg-brand-500 text-white"
                        : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-white"
                    }`}
                    onClick={() => setCategoryId(cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {loadingMenu ? (
                <div className="flex justify-center py-16">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
                </div>
              ) : availableProducts.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-400">Aucun produit disponible</p>
              ) : (
                availableProducts.map((product) => (
                  <button
                    key={product.id}
                    className="flex w-full items-center justify-between gap-3 border-b border-gray-50 px-5 py-3 text-left hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                    onClick={() => handleSelectProduct(product)}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">{product.name}</p>
                      {product.description && (
                        <p className="truncate text-xs text-gray-400">{product.description}</p>
                      )}
                    </div>
                    <span className="text-sm font-bold text-brand-500">{Number(product.price || 0).toFixed(3)} TND</span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="mb-5">
                <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedProduct?.name}</p>
                <p className="text-sm font-semibold text-brand-500">{Number(selectedProduct?.price || 0).toFixed(3)} TND</p>
              </div>

              {(selectedProduct?.options || []).map((option) => {
                const items = getOptionItems(option);
                if (!items.length) return null;
                return (
                  <div key={option.id} className="mb-5 rounded-xl border border-gray-100 p-4 dark:border-navy-700">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <p className="font-bold text-navy-700 dark:text-white">{option.name}</p>
                      {option.min > 0 && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-600">Obligatoire</span>}
                    </div>
                    <div className="space-y-2">
                      {items.map((item) => {
                        const checked = !!selectedItems[itemKey(option.id, item.id)];
                        return (
                          <button
                            key={item.id}
                            className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${
                              checked
                                ? "border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-900/20"
                                : "border-gray-100 text-gray-600 dark:border-navy-700 dark:text-white"
                            }`}
                            onClick={() => toggleItem(option, item)}
                          >
                            <span>{item.name}</span>
                            <span>{Number(item.price || 0) > 0 ? `+${Number(item.price).toFixed(3)} TND` : ""}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <textarea
                className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-navy-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                rows={3}
                placeholder="Instructions speciales..."
                value={specifications}
                onChange={(event) => setSpecifications(event.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 border-t border-gray-100 p-4 dark:border-navy-700">
              <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1 dark:bg-navy-700">
                <button className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-gray-600 dark:bg-navy-800 dark:text-white" onClick={() => setQuantity((value) => Math.max(1, value - 1))}>
                  <MdRemove />
                </button>
                <span className="w-8 text-center text-sm font-bold text-navy-700 dark:text-white">{quantity}</span>
                <button className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-gray-600 dark:bg-navy-800 dark:text-white" onClick={() => setQuantity((value) => value + 1)}>
                  <MdAdd />
                </button>
              </div>
              <button
                className="flex-1 rounded-xl bg-brand-500 py-3 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-50"
                disabled={!isValid() || saving}
                onClick={handleConfirm}
              >
                {saving ? "Enregistrement..." : `${replaceTarget ? "Remplacer" : "Ajouter"} - ${calcTotal().toFixed(3)} TND`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Order detail modal — full featured ───────────────────────────────────────
function OrderDetailModal({ orderId, restaurantId, restaurant, onClose, onAccept, onRefuse, onPrepare, onReady, onOrderUpdated }) {
  const [order,     setOrder]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(null); // orderProductId being saved
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [pickerSaving, setPickerSaving] = useState(false);
  const [outOfStock, setOutOfStock] = useState({});

  const handleOutOfStock = async (productId) => {
    if (!productId || outOfStock[productId]) return;
    try {
      await setProductAvailability(productId, false);
      setOutOfStock((prev) => ({ ...prev, [productId]: true }));
    } catch {}
  };

  const reloadOrder = useCallback(async () => {
    if (!orderId) return null;
    const data = await getOrderDetail(orderId);
    const nextOrder = data.data || data;
    setOrder(nextOrder);
    return nextOrder;
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    reloadOrder()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId, reloadOrder]);

  if (!orderId) return null;

  const st         = order ? (STATUS[order.status_id] || STATUS[1]) : null;
  const total      = order ? (order.total_price || order.total || 0) : 0;
  const canEdit    = order?.status_id === 1; // partner can edit products only before confirmation
  const canAccept  = order?.status_id === 1;
  const canPrepare = order?.status_id === 2;
  const canReady   = order?.status_id === 3 && !order?.order_ready_on;

  const handleQtyChange = async (product, delta) => {
    const currentQty = product.quantity || product.pivot?.quantity || 1;
    const newQty     = currentQty + delta;
    const pivotId    = getPivotId(product);
    if (newQty < 1) {
      handleRemove(product);
      return;
    }
    setSaving(pivotId);
    try {
      await updateOrderProduct(order.id, pivotId, {
        product_id: product.product_id || product.product?.id || product.id,
        quantity: newQty,
        items: getItemIds(product),
        specifications: product.pivot?.specifications || product.specifications || "",
      });
      await reloadOrder();
      onOrderUpdated?.(order.id);
    } catch {}
    setSaving(null);
  };

  const handleRemove = async (product) => {
    const pivotId = getPivotId(product);
    setSaving(pivotId);
    try {
      await deleteOrderProduct(order.id, pivotId);
      await reloadOrder();
      onOrderUpdated?.(order.id);
    } catch {}
    setSaving(null);
  };

  const handleOpenAddProduct = () => {
    setReplaceTarget(null);
    setShowProductPicker(true);
  };

  const handleReplaceProduct = (product) => {
    setReplaceTarget({
      orderProductId: getPivotId(product),
      name: getProductName(product),
    });
    setShowProductPicker(true);
  };

  const handleProductPicked = async (productId, payload) => {
    setPickerSaving(true);
    try {
      if (replaceTarget?.orderProductId) {
        await deleteOrderProduct(order.id, replaceTarget.orderProductId);
      }
      await addOrderProduct(order.id, productId, payload);
      await reloadOrder();
      onOrderUpdated?.(order.id);
      setShowProductPicker(false);
      setReplaceTarget(null);
    } catch {
    } finally {
      setPickerSaving(false);
    }
  };

  const handleReady = async () => {
    if (!order?.id) return;
    try {
      await onReady(order.id);
      await reloadOrder();
      onOrderUpdated?.(order.id);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-navy-800 shadow-xl overflow-y-auto max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-navy-700 sticky top-0 bg-white dark:bg-navy-800 z-10">
          {loading ? (
            <div className="h-6 w-48 bg-gray-100 dark:bg-navy-700 rounded animate-pulse" />
          ) : (
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-lg text-navy-700 dark:text-white">
                  #{orderReference(order)}
                </h3>
                {order?.orderType === "takeaway" && (
                  <span className="rounded-full bg-amber-100 text-amber-600 text-xs font-bold px-2 py-0.5">
                    À emporter
                  </span>
                )}
                {st && (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.color}`}>
                    {st.label}
                  </span>
                )}
              </div>
              {order?.created_at && (
                <p className="text-xs text-gray-400 mt-0.5">Reçue le {fmt(order.created_at)}</p>
              )}
            </div>
          )}
          <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100 dark:hover:bg-navy-700 flex-shrink-0">
            <MdClose className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : !order ? (
          <div className="flex justify-center py-12 text-gray-400 text-sm">Commande introuvable</div>
        ) : (
          <div className="p-5 space-y-5 flex-1">
            {/* Client + Payment */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Client</p>
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                  {order.user?.first_name
                    ? `${order.user.first_name} ${order.user.last_name || ""}`.trim()
                    : order.user?.name || "—"}
                </p>
                {order.user?.phone && (
                  <p className="text-xs text-gray-500 mt-0.5">{order.user.phone}</p>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Paiement</p>
                <p className="text-sm font-medium text-navy-700 dark:text-white flex items-center gap-1">
                  <MdPayment className="h-4 w-4 text-gray-400" />
                  {order.payementMode?.name || order.payement_mode_id === 1 ? "Espèces" : "En ligne"}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Canal de réception</p>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${orderReceptionClass(orderReceptionMode(order))}`}>
                  {orderReceptionLabel(orderReceptionMode(order))}
                </span>
              </div>
            </div>

            {/* Address */}
            {order.address && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1 flex items-center gap-1">
                  <MdLocationOn className="h-3.5 w-3.5" /> Adresse
                </p>
                <p className="text-sm text-navy-700 dark:text-white">
                  {order.address.street || order.address.label || "—"}
                </p>
              </div>
            )}

            {/* Products */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                Produits
                {canEdit && <span className="ml-2 text-blue-500">(modifiable avant confirmation)</span>}
              </p>
              <div className="space-y-2">
                {(order.products || []).map((p, i) => {
                  const pivotId  = getPivotId(p);
                  const productId = p.product_id || (p.product && p.product.id) || p.id;
                  const qty      = p.quantity || p.pivot?.quantity || 1;
                  const unitPrice = p.price || p.base_price || 0;
                  const lineTotal = p.total_price || p.pivot?.total || (unitPrice * qty) || 0;
                  const isSaving = saving === pivotId;
                  const isOutOfStock = !!outOfStock[productId];
                  const optionLabels = Array.isArray(p.items)
                    ? p.items.map((item) => item?.name || item).join(", ")
                    : "";
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded-xl bg-gray-50 dark:bg-navy-700 px-3 py-2.5"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                          {getProductName(p)}
                        </p>
                        {(p.pivot?.specifications || p.specifications) && (
                          <p className="text-xs text-gray-400 italic mt-0.5">
                            {p.pivot?.specifications || p.specifications}
                          </p>
                        )}
                        {optionLabels && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Options: {optionLabels}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleOutOfStock(productId)}
                        disabled={isOutOfStock}
                        title="Marquer le produit en rupture de stock"
                        className={`flex-shrink-0 self-start rounded-lg border px-2 py-1 text-[10px] font-bold ${
                          isOutOfStock
                            ? "border-red-200 bg-red-50 text-red-400"
                            : "border-red-200 text-red-500 hover:bg-red-50 dark:border-navy-600"
                        }`}
                      >
                        {isOutOfStock ? "En rupture" : "Rupture de stock"}
                      </button>
                      {canEdit ? (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 dark:border-navy-600 text-gray-500 hover:bg-gray-200 disabled:opacity-40"
                            onClick={() => handleQtyChange(p, -1)}
                            disabled={isSaving}
                          >
                            <MdRemove className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-5 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {isSaving ? "…" : qty}
                          </span>
                          <button
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 dark:border-navy-600 text-gray-500 hover:bg-gray-200 disabled:opacity-40"
                            onClick={() => handleQtyChange(p, 1)}
                            disabled={isSaving}
                          >
                            <MdAdd className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="ml-1 flex h-6 w-6 items-center justify-center rounded-full text-red-400 hover:bg-red-50 disabled:opacity-40"
                            onClick={() => handleRemove(p)}
                            disabled={isSaving}
                          >
                            <MdDelete className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="flex h-6 w-6 items-center justify-center rounded-full text-orange-500 hover:bg-orange-50 disabled:opacity-40"
                            onClick={() => handleReplaceProduct(p)}
                            disabled={isSaving}
                            title="Remplacer"
                          >
                            <MdSwapHoriz className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-gray-400">×{qty}</span>
                          <span className="text-sm font-semibold text-navy-700 dark:text-white">
                            {Number(lineTotal).toFixed(3)} TND
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {canEdit && (
                <button
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-brand-500 bg-brand-50 py-3 text-sm font-bold text-brand-500 hover:bg-brand-100 dark:bg-brand-900/10"
                  onClick={handleOpenAddProduct}
                >
                  <MdAdd className="h-4 w-4" />
                  Ajouter un article
                </button>
              )}
            </div>

            {/* Total */}
            <div className="flex justify-between border-t border-gray-100 dark:border-navy-700 pt-3">
              <span className="font-bold text-navy-700 dark:text-white">Total</span>
              <span className="font-bold text-brand-500 text-lg">{Number(total).toFixed(3)} TND</span>
            </div>

            {/* Prep time */}
            {order.estimated_preparation_time > 0 && (
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <MdTimer className="h-4 w-4" />
                Préparation estimée : {order.estimated_preparation_time} min
              </p>
            )}

            {order.order_ready_on && (
              <p className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                <MdCheck className="h-4 w-4" />
                Commande prete depuis {fmt(order.order_ready_on)}
              </p>
            )}

            {/* Delivery info */}
            {[4, 5].includes(order.status_id) && (
              <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 px-4 py-3 flex items-center gap-2">
                <MdDeliveryDining className="h-5 w-5 text-purple-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    {order.status_id === 4 ? "Livreur en route" : "Commande livrée"}
                  </p>
                  {order.delivery && (
                    <p className="text-xs text-purple-500 mt-0.5">
                      {order.delivery.first_name || order.delivery.name || "Livreur assigné"}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Refusal reason */}
            {order.status_id === 6 && order.raison && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 px-4 py-3">
                <p className="text-xs font-semibold text-red-400 uppercase mb-1">Motif de refus</p>
                <p className="text-sm text-red-600 dark:text-red-400">{order.raison}</p>
              </div>
            )}

            {/* Timeline */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2 flex items-center gap-1">
                <MdAccessTime className="h-3.5 w-3.5" /> Historique
              </p>
              <div className="space-y-2">
                {[
                  { label: "Commande reçue",   ts: order.created_at,    color: "bg-blue-500"   },
                  { label: "Confirmée",         ts: order.confirmed_at,  color: "bg-yellow-500" },
                  { label: "Commande prete",    ts: order.order_ready_on, color: "bg-green-500"  },
                  { label: "Livreur en route",  ts: order.onroad_at,     color: "bg-purple-500" },
                  { label: "Livrée",            ts: order.delivred_at,   color: "bg-green-500"  },
                  { label: "Annulée",           ts: order.canceled_at,   color: "bg-red-500"    },
                ]
                  .filter((e) => !!e.ts)
                  .map((e, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-xs">
                      <MdCircle className={`h-2 w-2 flex-shrink-0 ${e.color} rounded-full`} />
                      <span className="text-gray-500 dark:text-gray-400 flex-1">{e.label}</span>
                      <span className="text-navy-700 dark:text-white font-medium">{fmt(e.ts)}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Partner actions */}
            {canAccept && (
              <div className="flex gap-2 pt-1">
                <button
                  className="flex-1 rounded-xl bg-green-500 py-3 text-sm font-bold text-white hover:bg-green-600"
                  onClick={() => { onClose(); onAccept(order); }}
                >
                  <MdCheck className="inline h-4 w-4 mr-1" />
                  Accepter
                </button>
                <button
                  className="flex-1 rounded-xl border border-red-200 py-3 text-sm font-bold text-red-500 hover:bg-red-50"
                  onClick={() => { onClose(); onRefuse(order); }}
                >
                  Refuser
                </button>
              </div>
            )}
            {canPrepare && (
              <button
                className="w-full rounded-xl bg-orange-500 py-3 text-sm font-bold text-white hover:bg-orange-600"
                onClick={() => { onPrepare(order.id); onClose(); }}
              >
                <MdStorefront className="inline h-4 w-4 mr-1" />
                Mettre en préparation
              </button>
            )}
            {canReady && (
              <button
                className="w-full rounded-xl bg-green-500 py-3 text-sm font-bold text-white hover:bg-green-600"
                onClick={handleReady}
              >
                <MdCheck className="inline h-4 w-4 mr-1" />
                Marquer comme prete
              </button>
            )}
            {order.status_id >= 2 && (
              <button
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 py-3 text-sm font-bold text-navy-700 hover:bg-gray-50 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
                onClick={() => printOrderTicket(order, restaurant)}
              >
                <MdPrint className="h-4 w-4" />
                Imprimer le ticket
              </button>
            )}
          </div>
        )}
      </div>
      <ProductPickerModal
        visible={showProductPicker}
        restaurantId={restaurantId}
        replaceTarget={replaceTarget}
        onClose={() => {
          setShowProductPicker(false);
          setReplaceTarget(null);
        }}
        onConfirm={handleProductPicked}
        saving={pickerSaving}
      />
    </div>
  );
}

// ── Main Orders page ──────────────────────────────────────────────────────────
export default function Orders() {
  const { restaurantId, restaurant } = useAuth();
  const [orders,      setOrders]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [detailId,    setDetailId]    = useState(null); // id of order shown in detail modal
  const [acceptOrder, setAcceptOrder] = useState(null);
  const [refuseOrder, setRefuseOrder] = useState(null);
  const [dateFilter,  setDateFilter]  = useState(() => localDateString());
  const [searchRef,   setSearchRef]   = useState("");
  const pollingRef = useRef(null);

  const { newOrderIds, acknowledge } = useNewOrderAlert(orders);

  const load = useCallback(async (silent = false) => {
    if (!restaurantId) return;
    if (!silent) setLoading(true);
    try {
      const params = dateFilter ? { orderDate: dateFilter } : {};
      const data = await getOrders(restaurantId, params);
      setOrders(Array.isArray(data) ? data : data.data || []);
    } catch {}
    finally { if (!silent) setLoading(false); }
  }, [restaurantId, dateFilter]);

  useEffect(() => {
    load();
    pollingRef.current = setInterval(() => load(true), 60000);
    return () => clearInterval(pollingRef.current);
  }, [load]);

  useEffect(() => {
    const handleNewOrder = () => load(true);
    window.addEventListener("restaurant:new-order", handleNewOrder);
    return () => window.removeEventListener("restaurant:new-order", handleNewOrder);
  }, [load]);

  const handleAccept = async (orderId, prepTimeMins) => {
    try {
      await updateOrderStatus(orderId, 2);
      await setPreparationTime(orderId, prepTimeMins);
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, status_id: 2, estimated_preparation_time: prepTimeMins } : o
      ));
    } catch {}
    setAcceptOrder(null);
    acknowledge();
  };

  const handleRefuse = async (orderId, motif) => {
    try {
      await cancelOrder(orderId, motif);
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, status_id: 6, raison: motif } : o
      ));
    } catch {}
    setRefuseOrder(null);
    acknowledge();
  };

  const handlePrepare = async (orderId) => {
    try {
      await updateOrderStatus(orderId, 3);
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, status_id: 3 } : o
      ));
    } catch {}
  };

  const handleReady = async (orderId) => {
    try {
      const data = await markOrderReady(orderId);
      const readyOrder = data?.data || data || {};
      const readyAt = readyOrder.order_ready_on || new Date().toISOString();
      setOrders((prev) => prev.map((o) =>
        o.id === orderId ? { ...o, ...readyOrder, order_ready_on: readyAt } : o
      ));
    } catch {}
  };

  // Fetch the full order (options, notes, address) then open the print dialog.
  const handlePrint = async (order) => {
    try {
      const data = await getOrderDetail(order.id);
      printOrderTicket(data.data || data, restaurant);
    } catch {
      // Fall back to the summary data already on the card.
      printOrderTicket(order, restaurant);
    }
  };

  const filtered = orders.filter((o) =>
    !searchRef || orderReference(o).toLowerCase().includes(searchRef.toLowerCase())
  );

  return (
    <div className="mt-3">
      {/* New-order alert banner */}
      {newOrderIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl bg-brand-500 px-5 py-3 shadow-lg animate-pulse">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
              <MdNotifications className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="font-bold text-white text-sm">
                🔔 {newOrderIds.length} nouvelle{newOrderIds.length > 1 ? "s" : ""} commande{newOrderIds.length > 1 ? "s" : ""} !
              </p>
              <p className="text-xs text-white/80">Acceptez ou refusez chaque commande</p>
            </div>
          </div>
          <button
            onClick={acknowledge}
            className="flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-sm font-bold text-brand-500 hover:bg-brand-50 flex-shrink-0"
          >
            <MdVolumeUp className="h-4 w-4" />
            Couper le son
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <IoFastFood className="h-7 w-7 text-brand-500" />
          Commandes
        </h2>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Référence..."
            className="rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-3 py-2 text-sm text-navy-700 dark:text-white w-32"
            value={searchRef}
            onChange={(e) => setSearchRef(e.target.value)}
          />
          <input
            type="date"
            className="rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-3 py-2 text-sm text-navy-700 dark:text-white"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
          <button
            onClick={() => load()}
            className="flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <MdRefresh className="h-4 w-4" />
            Actualiser
          </button>
        </div>
      </div>

      <OperationalStatusBar />

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
          {STATUS_GROUPS.map((group) => {
            const groupOrders = filtered.filter((o) => group.ids.includes(o.status_id));
            return (
              <div key={group.label}>
                <div className={`mb-3 flex items-center justify-between border-l-4 pl-3 ${group.border}`}>
                  <h3 className="font-bold text-navy-700 dark:text-white text-sm">{group.label}</h3>
                  <span className="rounded-full bg-gray-100 dark:bg-navy-700 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-white">
                    {groupOrders.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {groupOrders.length === 0 ? (
                    <p className="text-center text-xs text-gray-400 py-8">Aucune commande</p>
                  ) : (
                    groupOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onAccept={(o) => { setAcceptOrder(o); acknowledge(); }}
                        onRefuse={(o) => { setRefuseOrder(o); acknowledge(); }}
                        onPrepare={handlePrepare}
                        onReady={handleReady}
                        onPrint={handlePrint}
                        onSelect={(o) => {
                          setDetailId(o.id);
                          if (o.status_id === 1) acknowledge();
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full detail modal */}
      {detailId && !acceptOrder && !refuseOrder && (
        <OrderDetailModal
          orderId={detailId}
          restaurantId={restaurantId}
          restaurant={restaurant}
          onClose={() => setDetailId(null)}
          onAccept={(o) => { setDetailId(null); setAcceptOrder(o); }}
          onRefuse={(o) => { setDetailId(null); setRefuseOrder(o); }}
          onPrepare={(id) => { handlePrepare(id); setDetailId(null); }}
          onReady={handleReady}
          onOrderUpdated={() => load(true)}
        />
      )}

      {acceptOrder && (
        <AcceptModal order={acceptOrder} onConfirm={handleAccept} onClose={() => setAcceptOrder(null)} />
      )}
      {refuseOrder && (
        <RefuseModal order={refuseOrder} onConfirm={handleRefuse} onClose={() => setRefuseOrder(null)} />
      )}
    </div>
  );
}
